import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiClientError } from "@/lib/api-client";
import { useFormRuntime } from "./use-form-runtime";
import { extractValidationIssues } from "./use-submit-form";
import { FIELD_RENDERERS } from "./field-renderers/field-registry";
import { SaveStatusIndicator } from "./save-status-indicator";

// Port of /app/portal/forms/[slug]/page.tsx. The runtime view.
//
// Consumes useFormRuntime(slug) which wires:
//   • useFormDetail(slug) for the form + draft
//   • react-hook-form + zodResolver(buildSubmissionSchema(fields))
//   • 500ms debounced auto-save (via useSaveDraft)
//   • useSubmitForm for the promote
//   • use{Upload,Remove}FormAttachment for file fields
//
// Auto-accept submissions navigate to /portal/dashboard?submitted=deal|lead
// (matches the legacy redirect). Standard submissions navigate to the
// per-slug submitted page with ?submissionId=<id>.

export function FormDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [discardConfirmed, setDiscardConfirmed] = useState(false);

  const runtime = useFormRuntime(slug);
  const { form, detail, draft, saveStatus, submit, discard } = runtime;

  if (detail.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    const err = detail.error;
    const notFound = err instanceof ApiClientError && err.status === 404;
    return (
      <div className="mx-auto max-w-2xl rounded-xl border bg-card p-8 text-center">
        <h2 className="font-display text-xl font-semibold tracking-tight">
          {notFound ? "Form not found" : "Couldn’t load this form"}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {notFound
            ? "It may have been unpublished. Browse the available forms instead."
            : "Please refresh the page. If the problem persists, contact support."}
        </p>
        <Link
          to="/portal/forms"
          className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to forms
        </Link>
      </div>
    );
  }

  const { form: formMeta } = detail.data;
  const definition = formMeta.definition;
  const submissionId = draft?.submissionId ?? null;

  function onSubmit(values: Record<string, unknown>) {
    if (!submissionId) {
      toast.error("Draft not ready yet — try again in a moment.");
      return;
    }
    submit.mutate(
      // payload was already auto-saved server-side; the submit call carries
      // attachment ids extracted from the local form values.
      {
        attachment_ids: extractAttachmentIds(values),
      },
      {
        onSuccess: (result) => {
          if (result.autoAccepted && result.downstream?.kind) {
            navigate(
              `/portal/dashboard?submitted=${encodeURIComponent(
                result.downstream.kind,
              )}`,
              { replace: true },
            );
            return;
          }
          navigate(
            `/portal/forms/${slug}/submitted?submissionId=${encodeURIComponent(
              result.id,
            )}`,
            { replace: true },
          );
        },
        onError: (err) => {
          const issues = extractValidationIssues(err);
          if (issues) {
            for (const issue of issues) {
              const path = issue.path?.[0];
              if (typeof path === "string") {
                form.setError(path, { message: issue.message });
              }
            }
            toast.error("Some fields need attention before submitting.");
            return;
          }
          toast.error(err.message ?? "Failed to submit form");
        },
      },
    );
  }

  function onDiscard() {
    if (!submissionId) return;
    if (!discardConfirmed) {
      setDiscardConfirmed(true);
      setTimeout(() => setDiscardConfirmed(false), 4000);
      return;
    }
    discard.mutate(
      { submissionId },
      {
        onSuccess: () => {
          toast.success("Draft discarded");
          navigate("/portal/forms");
        },
      },
    );
  }

  return (
    <div>
      <div className="mb-7 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
            {definition.title ?? formMeta.name}
          </h1>
          {(formMeta.description ?? definition.description) ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {formMeta.description ?? definition.description}
            </p>
          ) : null}
        </div>
        <SaveStatusIndicator status={saveStatus} className="mt-1 shrink-0" />
      </div>

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="mx-auto max-w-2xl space-y-6"
      >
        {definition.sections.map((section, sIdx) => (
          <div key={section.id ?? sIdx} className="space-y-4">
            {section.heading ? (
              <div className="border-b border-border/70 pb-2">
                <h3 className="font-display text-base font-semibold tracking-tight text-foreground">
                  {section.heading}
                </h3>
                {section.description ? (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {section.description}
                  </p>
                ) : null}
              </div>
            ) : null}
            {section.fields.map((field) => {
              const Renderer = FIELD_RENDERERS[field.kind];
              if (!Renderer) return null;
              return (
                <Renderer
                  key={field.id}
                  field={field}
                  control={form.control}
                  disabled={submit.isPending}
                  slug={slug}
                  submissionId={submissionId}
                />
              );
            })}
          </div>
        ))}

        <div className="flex items-center justify-between pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={!submissionId || discard.isPending || submit.isPending}
            className={
              discardConfirmed
                ? "text-destructive hover:text-destructive"
                : "text-muted-foreground"
            }
          >
            {discardConfirmed ? "Confirm discard" : "Discard draft"}
          </Button>
          <Button
            type="submit"
            disabled={!submissionId || submit.isPending}
          >
            {submit.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {definition.submitButtonLabel ?? "Submit"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// File fields carry their attachment metadata under their field.name key
// in the form values. Pull the ids out for the submit body.
function extractAttachmentIds(values: Record<string, unknown>): string[] {
  const ids: string[] = [];
  for (const v of Object.values(values)) {
    if (
      v &&
      typeof v === "object" &&
      "id" in (v as object) &&
      typeof (v as { id?: unknown }).id === "string"
    ) {
      ids.push((v as { id: string }).id);
    }
  }
  return ids;
}
